import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  FileText, Upload, Sparkles, Copy, Check, Trash2,
  FolderOpen, Download, Clock, X, Edit3, Star
} from "lucide-react";
import { toast } from "sonner";
import { EngineSelect, EmploymentSelect } from "../components/FlowSelectors";
import { ENGINE_KEYS_LITE } from "../lib/models";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREFS_KEY = "resumeai_defaults";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}"); } catch { return {}; } };
const savePrefs = (p) => { const e = loadPrefs(); localStorage.setItem(PREFS_KEY, JSON.stringify({ ...e, ...p })); };

export default function CoverLetterPage() {
  const [savedResumes, setSavedResumes] = useState([]);
  const [defaultResumeId, setDefaultResumeId] = useState("");
  const [selectedResumeId, setSelectedResumeId] = useState("");
  const [resumeSource, setResumeSource] = useState("default");
  const [uploadedText, setUploadedText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editedLetter, setEditedLetter] = useState("");
  const [copied, setCopied] = useState(false);
  const [savedLetters, setSavedLetters] = useState([]);
  const [loadingLetters, setLoadingLetters] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showCompanyPopup, setShowCompanyPopup] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState("");
  const [isThirdParty, setIsThirdParty] = useState(false);
  const [employmentType, setEmploymentType] = useState(loadPrefs().employmentType ?? "contract-c2c");
  const [aiModel, setAiModel] = useState(ENGINE_KEYS_LITE.includes(loadPrefs().aiModel) ? loadPrefs().aiModel : "glm-5.2");

  useEffect(() => { fetchResumes(); fetchLetters(); }, []);

  const fetchResumes = async () => {
    try {
      const res = await axios.get(`${API}/resumes`);
      setSavedResumes(res.data || []);
      const def = res.data?.find(r => r.is_default);
      if (def) { setDefaultResumeId(def.id); }
      if (res.data.length > 0) { setResumeSource("default"); }
      else setResumeSource("paste");
    } catch { /* silent */ }
  };

  const fetchLetters = async () => {
    try { const res = await axios.get(`${API}/cover-letters`); setSavedLetters(res.data); }
    catch { /* silent */ }
    finally { setLoadingLetters(false); }
  };

  const handleGenerate = async () => {
    const hasResume = resumeSource === "default" ? !!defaultResumeId : resumeSource === "saved" ? !!selectedResumeId : !!uploadedText.trim();
    if (!hasResume && resumeSource !== "default") {
      toast.error("Resume is required"); return;
    }
    if (!jobDescription.trim()) { toast.error("Job description is required"); return; }

    // Always show company details popup — never auto-fill
    setPendingCompanyName(companyName);
    setShowCompanyPopup(true);
  };

  const handleCompanyPopupSubmit = async () => {
    if (!pendingCompanyName.trim()) {
      toast.error("Company name is required"); return;
    }
    const cn = pendingCompanyName.trim();
    setCompanyName(cn);
    setShowCompanyPopup(false);
    await runGeneration(cn, isThirdParty, employmentType);
  };

  const runGeneration = async (cn, thirdParty = false, empType = "") => {
    setGenerating(true);
    try {
      const payload = {
        job_description: jobDescription,
        job_title: jobTitle,
        company_name: cn,
        is_third_party: thirdParty,
        employment_type: empType,
        ai_model: aiModel,
      };
      if (resumeSource === "saved") payload.saved_resume_id = selectedResumeId;
      else if (resumeSource === "default" && defaultResumeId) payload.saved_resume_id = defaultResumeId;
      else payload.resume_text = uploadedText;

      const submitRes = await axios.post(`${API}/cover-letter/generate`, payload);
      const jobId = submitRes.data.job_id;

      const result = await new Promise((resolve, reject) => {
        const start = Date.now();
        const poll = async () => {
          if (Date.now() - start > 120000) { reject(new Error("Timed out")); return; }
          try {
            const statusRes = await axios.get(`${API}/cover-letter/status/${jobId}`);
            if (statusRes.data.status === "completed" && statusRes.data.result) {
              resolve(statusRes.data.result);
            } else if (statusRes.data.status === "failed") {
              reject(new Error(statusRes.data.error || "Generation failed"));
            } else { setTimeout(poll, 2000); }
          } catch { setTimeout(poll, 2000); }
        };
        poll();
      });

      setCoverLetter(result.cover_letter);
      setEditedLetter(result.cover_letter);
      setEditMode(false);
      toast.success("Cover letter generated!");
      fetchLetters();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to generate");
    } finally { setGenerating(false); }
  };

  const handleCopy = () => {
    const text = editMode ? editedLetter : coverLetter;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadDocx = async () => {
    const text = editMode ? editedLetter : coverLetter;
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();  // 612
    const pageH = doc.internal.pageSize.getHeight();  // 792
    const M = 72; // 1-inch margins all sides
    const usableW = pageW - M * 2; // ~468pt
    const fontSize = 11;
    const lineH = fontSize * 1.3; // ~14.3pt (1.3 line spacing for readability)
    const paraGap = lineH * 0.6;

    // ── Parse cover letter text into structured sections ──
    const allLines = text.split("\n");
    let recipientLines = [];
    let subjectLine = "";
    let salutation = "";
    let bodyParagraphs = [];
    let closingLine = "";
    let signatureName = "";
    let contactLines = [];

    let phase = "header";
    let currentPara = [];

    for (const raw of allLines) {
      const line = raw.trim();

      // Skip date from LLM — we always use current date
      if (phase === "header" && /(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}/.test(line)) {
        continue;
      }

      if (!subjectLine && /^(?:Re:|Subject:|RE:)/i.test(line)) {
        subjectLine = line;
        phase = "body";
        continue;
      }

      if (!salutation && /^(?:Dear|Hello|Hi)\s/i.test(line)) {
        salutation = line;
        phase = "body";
        continue;
      }

      if (phase === "body" && /^(?:Sincerely|Best regards|Regards|Warm regards|Kind regards)\s*,?\s*$/i.test(line)) {
        if (currentPara.length > 0) {
          bodyParagraphs.push(currentPara.join(" "));
          currentPara = [];
        }
        closingLine = line.replace(/,?\s*$/, ",");
        phase = "closing";
        continue;
      }

      if (phase === "closing") {
        if (!line) continue;
        if (!signatureName && /^[A-Z][a-z]+/.test(line) && line.length < 50 && !line.includes("@") && !/\d{3}/.test(line) && !line.includes("linkedin")) {
          signatureName = line;
        } else if (line) {
          contactLines.push(line);
        }
        continue;
      }

      if (phase === "header" && line && !salutation && !subjectLine) {
        recipientLines.push(line);
        continue;
      }

      if (phase === "body") {
        if (!line) {
          if (currentPara.length > 0) {
            bodyParagraphs.push(currentPara.join(" "));
            currentPara = [];
          }
        } else {
          currentPara.push(line);
        }
      }
    }
    if (currentPara.length > 0) bodyParagraphs.push(currentPara.join(" "));

    // Always use CURRENT date
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    if (!salutation) salutation = "Dear Hiring Manager,";
    if (!closingLine) closingLine = "Sincerely,";

    // ── ADAPTIVE FIT: Calculate total height, shrink if needed ──
    // We must fit EVERYTHING on exactly one page. If it doesn't fit at
    // default size, we reduce line-spacing (and font if necessary) until it does.
    const availableH = pageH - M * 2; // 792 - 144 = 648pt usable height

    const calcHeight = (fs, lh) => {
      doc.setFontSize(fs);
      let h = 0;
      h += lh * 2;                                          // date + blank line
      h += recipientLines.length * lh;                      // recipient
      if (recipientLines.length > 0) h += lh * 0.5;
      if (subjectLine) h += lh * 1.5;                       // subject + gap
      h += lh * 1.5;                                        // salutation + gap
      for (let i = 0; i < bodyParagraphs.length; i++) {     // body
        const lines = doc.splitTextToSize(bodyParagraphs[i], usableW);
        h += lines.length * lh;
        if (i < bodyParagraphs.length - 1) h += lh * 0.6;
      }
      h += lh * 0.3;                                        // gap before closing
      const closingWrapped = doc.splitTextToSize(closingLine, usableW);
      h += closingWrapped.length * lh;                         // closing line(s)
      h += lh * 0.5;                                        // gap after closing
      if (signatureName) h += lh;                            // name
      h += contactLines.length * lh;                         // contacts
      return h;
    };

    // Try default settings first, then progressively shrink
    let finalFontSize = fontSize;
    let finalLineH = lineH;
    let totalH = calcHeight(finalFontSize, finalLineH);

    if (totalH > availableH) {
      // Try tighter line spacing (1.2 instead of 1.3)
      finalLineH = finalFontSize * 1.2;
      totalH = calcHeight(finalFontSize, finalLineH);
    }
    if (totalH > availableH) {
      // Try even tighter (1.15)
      finalLineH = finalFontSize * 1.15;
      totalH = calcHeight(finalFontSize, finalLineH);
    }
    if (totalH > availableH) {
      // Reduce font to 10.5pt
      finalFontSize = 10.5;
      finalLineH = finalFontSize * 1.2;
      totalH = calcHeight(finalFontSize, finalLineH);
    }
    if (totalH > availableH) {
      // Reduce font to 10pt with tight spacing
      finalFontSize = 10;
      finalLineH = finalFontSize * 1.15;
      totalH = calcHeight(finalFontSize, finalLineH);
    }

    // ── Render PDF — ALL same font, same size, same weight, left-aligned ──
    doc.setFont("times", "normal");
    doc.setFontSize(finalFontSize);
    doc.setTextColor(0, 0, 0);
    let y = M;

    // Helper: write a single line (with word-wrap safety)
    const writeLine = (txt) => {
      const wrapped = doc.splitTextToSize(txt, usableW);
      for (const wl of wrapped) {
        doc.text(wl, M, y);
        y += finalLineH;
      }
    };

    // Helper: write a paragraph with word-wrap, left-aligned
    const writeParagraph = (paragraph) => {
      const wrapped = doc.splitTextToSize(paragraph, usableW);
      for (const wl of wrapped) {
        writeLine(wl);
      }
    };

    // Date (current date, top-left)
    writeLine(dateStr);
    y += finalLineH; // extra blank line after date

    // Recipient block
    for (const rl of recipientLines) {
      writeLine(rl);
    }
    if (recipientLines.length > 0) y += finalLineH * 0.5;

    // Subject line — same font, NOT bold
    if (subjectLine) {
      writeLine(subjectLine);
      y += finalLineH * 0.5;
    }

    // Salutation
    writeLine(salutation);
    y += finalLineH * 0.5;

    // Body paragraphs — left-aligned, word-wrapped within margins
    for (let i = 0; i < bodyParagraphs.length; i++) {
      writeParagraph(bodyParagraphs[i]);
      if (i < bodyParagraphs.length - 1) y += finalLineH * 0.6;
    }

    // Closing — same font, NOT bold
    y += finalLineH * 0.3;
    writeLine(closingLine);
    y += finalLineH * 0.5;

    // Signature Name — same font, same size, NOT bold
    if (signatureName) {
      writeLine(signatureName);
    }

    // Contact details — same font, same size, same color
    for (const cl of contactLines) {
      writeLine(cl);
    }

    doc.save(`Cover_Letter_${companyName || "Draft"}_${jobTitle || "Role"}.pdf`);
  };

  const handleDeleteLetter = async (id) => {
    try { await axios.delete(`${API}/cover-letters/${id}`); setSavedLetters(prev => prev.filter(l => l.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const loadFromHistory = (letter) => {
    setCoverLetter(letter.result?.cover_letter || "");
    setEditedLetter(letter.result?.cover_letter || "");
    setJobTitle(letter.job_title || "");
    setCompanyName(letter.company_name || "");
    setShowHistory(false);
    setEditMode(false);
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch { return ""; }
  };

  const defaultResume = savedResumes.find(r => r.is_default);

  return (
    <div className="page-container fade-in" data-testid="cover-letter-page">
      <div className="page-header">
        <div>
          <h1>Cover Letter Generator</h1>
          <p className="page-subtitle">Generate tailored cover letters from your resume and job description.</p>
        </div>
        {savedLetters.length > 0 && (
          <button className="btn-outline" onClick={() => setShowHistory(!showHistory)} data-testid="cl-history-btn">
            <Clock size={14} /> History ({savedLetters.length})
          </button>
        )}
      </div>

      {/* Company Details Popup */}
      {showCompanyPopup && (
        <div className="modal-overlay" data-testid="company-name-popup">
          <div className="modal-box" style={{ maxWidth: "480px" }}>
            <div className="modal-header">
              <FileText size={18} style={{ color: "var(--accent)" }} />
              <h3>Company Details</h3>
              <button className="btn-icon" onClick={() => setShowCompanyPopup(false)}><X size={16} /></button>
            </div>
            <p className="modal-desc" style={{ marginBottom: "1rem", opacity: 0.7, fontSize: "0.85rem" }}>
              Enter the company details to generate a tailored cover letter.
            </p>

            {/* Company Name - Required */}
            <div style={{ marginBottom: "1rem" }}>
              <label className="email-label" style={{ marginBottom: "0.3rem", display: "block", fontWeight: 600 }}>Company Name *</label>
              <input
                className="email-input"
                placeholder="Enter company name..."
                value={pendingCompanyName}
                onChange={e => setPendingCompanyName(e.target.value)}
                autoFocus
                data-testid="company-name-input"
                style={{ width: "100%" }}
              />
            </div>

            {/* Third-Party Toggle */}
            <div style={{ marginBottom: "1rem" }}>
              <label className="email-label" style={{ marginBottom: "0.3rem", display: "block", fontWeight: 600 }}>Is this a Third-Party / Staffing Company?</label>
              <div className="source-toggle" style={{ marginTop: "0.3rem" }}>
                <button
                  className={`source-toggle-btn ${!isThirdParty ? "active" : ""}`}
                  onClick={() => setIsThirdParty(false)}
                  data-testid="third-party-no"
                  style={{ flex: 1 }}
                >
                  No — Direct Hire
                </button>
                <button
                  className={`source-toggle-btn ${isThirdParty ? "active" : ""}`}
                  onClick={() => setIsThirdParty(true)}
                  data-testid="third-party-yes"
                  style={{ flex: 1 }}
                >
                  Yes — Staffing / Agency
                </button>
              </div>
            </div>

            {/* Employment Type */}
            <div style={{ marginBottom: "1.2rem" }}>
              <label className="email-label" style={{ marginBottom: "0.3rem", display: "block", fontWeight: 600 }}>Employment Type</label>
              <EmploymentSelect value={employmentType} onChange={(v) => { setEmploymentType(v); savePrefs({ employmentType: v }); }} testid="cl-emp-select-popup" />
            </div>

            <button className="btn-secondary-optimize" onClick={handleCompanyPopupSubmit} data-testid="company-name-submit" style={{ width: "100%" }}>
              <Sparkles size={16} /> Generate Cover Letter
            </button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="cl-history" data-testid="cl-history">
          {savedLetters.map(l => (
            <div key={l.id} className="cl-history-card" onClick={() => loadFromHistory(l)}>
              <div className="cl-history-info">
                <span className="cl-history-title">{l.job_title || "Untitled"} — {l.company_name || "Unknown"}</span>
                <span className="cl-history-date">{formatDate(l.created_at)}</span>
              </div>
              <button className="btn-icon" onClick={e => { e.stopPropagation(); handleDeleteLetter(l.id); }} data-testid={`cl-delete-${l.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="email-gen-grid">
        {/* Left: Inputs */}
        <div className="card" data-testid="cl-input-card">
          <div className="card-header"><h3>Input</h3></div>

          {/* Resume Source */}
          <div className="email-section">
            <label className="email-label">Resume Source</label>
            {defaultResume && (
              <div className="email-default-resume-info" data-testid="cl-default-resume-info">
                <Star size={14} /> Using default: <strong>{defaultResume.display_name}</strong>
              </div>
            )}
            <div className="source-toggle" style={{ marginBottom: "0.5rem" }}>
              <button className={`source-toggle-btn ${resumeSource === "default" ? "active" : ""}`} onClick={() => setResumeSource("default")} data-testid="cl-source-default">
                <Star size={14} /> Default
              </button>
              {savedResumes.length > 1 && (
                <button className={`source-toggle-btn ${resumeSource === "saved" ? "active" : ""}`} onClick={() => setResumeSource("saved")} data-testid="cl-source-saved">
                  <FolderOpen size={14} /> Other
                </button>
              )}
              <button className={`source-toggle-btn ${resumeSource === "paste" ? "active" : ""}`} onClick={() => setResumeSource("paste")} data-testid="cl-source-paste">
                <FileText size={14} /> Paste Text
              </button>
            </div>
            {resumeSource === "saved" && (
              <select className="email-select" value={selectedResumeId} onChange={e => setSelectedResumeId(e.target.value)} data-testid="cl-resume-select">
                <option value="">Select resume...</option>
                {savedResumes.filter(r => !r.is_default).map(r => (
                  <option key={r.id} value={r.id}>{r.display_name}</option>
                ))}
              </select>
            )}
            {resumeSource === "paste" && (
              <textarea className="email-textarea" rows={5} placeholder="Paste your resume text here..." value={uploadedText} onChange={e => setUploadedText(e.target.value)} data-testid="cl-resume-text" />
            )}
          </div>

          {/* Job Details */}
          <div className="email-section">
            <label className="email-label">Job Title</label>
            <input className="email-input" placeholder="e.g. Senior Software Engineer" value={jobTitle} onChange={e => setJobTitle(e.target.value)} data-testid="cl-job-title" />
          </div>
          <div className="email-section">
            <label className="email-label">Job Description *</label>
            <textarea className="email-textarea" rows={8} placeholder="Paste the full job description here..." value={jobDescription} onChange={e => setJobDescription(e.target.value)} data-testid="cl-jd-input" />
          </div>

          {/* AI Engine + Employment Type */}
          <div className="email-section">
            <label className="email-label">AI Engine &amp; Employment Type</label>
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
              <EngineSelect value={aiModel} onChange={(m) => { setAiModel(m); savePrefs({ aiModel: m }); }} keys={ENGINE_KEYS_LITE} testid="cl-engine-select" className="flex-1" />
              <EmploymentSelect value={employmentType} onChange={(v) => { setEmploymentType(v); savePrefs({ employmentType: v }); }} testid="cl-employment-select" className="flex-1" />
            </div>
          </div>

          <button className="btn-secondary-optimize" onClick={handleGenerate} disabled={generating || !jobDescription.trim()} data-testid="cl-generate-btn">
            {generating ? <><Sparkles size={16} className="spin" /> Generating...</> : <><Sparkles size={16} /> Generate Cover Letter</>}
          </button>
        </div>

        {/* Right: Result */}
        <div className="card" data-testid="cl-result-card">
          <div className="card-header">
            <h3>Cover Letter</h3>
            {coverLetter && (
              <div style={{ display: "flex", gap: "0.3rem" }}>
                <button className={`btn-icon ${editMode ? "active" : ""}`} onClick={() => { setEditMode(!editMode); if (!editMode) setEditedLetter(coverLetter); }} title={editMode ? "Preview" : "Edit"} data-testid="cl-edit-btn">
                  <Edit3 size={16} />
                </button>
                <button className="btn-icon" onClick={handleCopy} title="Copy" data-testid="cl-copy-btn">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <button className="btn-icon" onClick={handleDownloadDocx} title="Download" data-testid="cl-download-btn">
                  <Download size={16} />
                </button>
              </div>
            )}
          </div>
          {coverLetter ? (
            editMode ? (
              <textarea className="cl-edit-textarea" value={editedLetter} onChange={e => setEditedLetter(e.target.value)} data-testid="cl-edit-textarea" />
            ) : (
              <pre className="email-result-pre" data-testid="cl-result">{editMode ? editedLetter : coverLetter}</pre>
            )
          ) : (
            <div className="email-empty-state" data-testid="cl-empty">
              <FileText size={28} />
              <p>Your cover letter will appear here.</p>
              <p className="email-empty-sub">Fill in the details and click Generate.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
